/**
 * SitButton Component for Poker Reborn
 *
 * Stylish "Sit Here" button for empty seats with:
 * - Gradient background
 * - Drop shadow
 * - Two-line text support
 * - Hover glow effect
 *
 * @see docs/screens/room/SEAT.md - Seat component documentation
 */

import { Container, Graphics, Text, FederatedPointerEvent, FillGradient } from 'pixi.js';
import { audioService } from '@/services/AudioService';

// =============================================================================
// TYPES
// =============================================================================

export interface SitButtonStyle {
  // Gradient colors (top to bottom)
  gradientFrom: number;
  gradientTo: number;

  // Hover gradient
  hoverGradientFrom: number;
  hoverGradientTo: number;

  // Press gradient
  pressGradientFrom: number;
  pressGradientTo: number;

  // Border
  borderColor: number;
  borderWidth: number;

  // Shadow
  shadowColor: number;
  shadowAlpha: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowBlur: number;

  // Text
  textColor: number;
  hoverTextColor: number;
  fontSize: number;
  fontFamily: string;
  lineHeight: number;

  // Text drop shadow
  textShadowColor: number;
  textShadowAlpha: number;
  textShadowBlur: number;

  // Hover glow
  glowColor: number;
  glowSize: number;
  glowAlpha: number;
}

export interface SitButtonOptions {
  /** Circle diameter */
  diameter: number;

  /** Show "Sit Here" label text (default: true) */
  showLabel?: boolean;

  /** Enable hover/click interactivity (default: true) */
  interactive?: boolean;

  /** Custom style overrides */
  style?: Partial<SitButtonStyle>;
}

// =============================================================================
// DEFAULT STYLE
// =============================================================================

const DEFAULT_STYLE: SitButtonStyle = {
  // Gradient (dark charcoal with cool blue tint)
  gradientFrom: 0x2a2a3e,
  gradientTo: 0x12121e,

  // Hover gradient (subtle lift)
  hoverGradientFrom: 0x35354d,
  hoverGradientTo: 0x1a1a2c,

  // Press gradient (deep dark)
  pressGradientFrom: 0x1e1e30,
  pressGradientTo: 0x0a0a14,

  // Border
  borderColor: 0x4a4a6e,
  borderWidth: 2,

  // Shadow
  shadowColor: 0x000000,
  shadowAlpha: 0.3,
  shadowOffsetX: 0,
  shadowOffsetY: 4,
  shadowBlur: 8,

  // Text
  textColor: 0x777799,
  hoverTextColor: 0xddeeff,
  fontSize: 14,
  fontFamily: 'Roboto, Arial, sans-serif',
  lineHeight: 1.2,

  // Text drop shadow
  textShadowColor: 0x000000,
  textShadowAlpha: 0.7,
  textShadowBlur: 3,

  // Hover glow
  glowColor: 0x5a7aff,
  glowSize: 12,
  glowAlpha: 0.4,
};

// =============================================================================
// SIT BUTTON COMPONENT
// =============================================================================

export class SitButton extends Container {
  private shadow: Graphics;
  private glow: Graphics;
  private background: Graphics;
  private labelText: Text;
  private style: SitButtonStyle;

  private _diameter: number;
  private _pressed = false;
  private _hovered = false;

  public onClick?: () => void;

  constructor(options: SitButtonOptions) {
    super();

    this.style = { ...DEFAULT_STYLE, ...options.style };
    this._diameter = options.diameter;

    // Create shadow (behind everything)
    this.shadow = new Graphics();
    this.addChild(this.shadow);

    // Create glow (behind background)
    this.glow = new Graphics();
    this.glow.visible = false;
    this.addChild(this.glow);

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create two-line label with drop shadow
    const radius = this._diameter / 2;
    const showLabel = options.showLabel !== false;
    this.labelText = new Text({
      text: 'Sit\nHere',
      style: {
        fill: this.style.textColor,
        fontSize: this.style.fontSize,
        fontFamily: this.style.fontFamily,
        fontWeight: 'normal',
        align: 'center',
        lineHeight: this.style.fontSize * this.style.lineHeight,
        dropShadow: {
          color: this.style.textShadowColor,
          alpha: this.style.textShadowAlpha,
          blur: this.style.textShadowBlur,
          distance: 0,
        },
      },
    });
    this.labelText.anchor.set(0.5);
    this.labelText.x = radius;
    this.labelText.y = radius;
    this.labelText.visible = showLabel;
    this.addChild(this.labelText);

    // Initial render
    this.render();

    // Set up interactivity
    const isInteractive = options.interactive !== false;
    this.eventMode = isInteractive ? 'static' : 'none';
    this.cursor = isInteractive ? 'pointer' : 'default';

    if (isInteractive) {
      this.on('pointerover', this.onPointerOver, this);
      this.on('pointerout', this.onPointerOut, this);
      this.on('pointerdown', this.onPointerDown, this);
      this.on('pointerup', this.onPointerUp, this);
      this.on('pointerupoutside', this.onPointerUpOutside, this);
    }
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Update button style */
  setStyle(style: Partial<SitButtonStyle>): void {
    this.style = { ...this.style, ...style };
    this.render();
  }

  /** Resize button */
  resize(diameter: number): void {
    this._diameter = diameter;
    const radius = diameter / 2;
    this.labelText.x = radius;
    this.labelText.y = radius;
    this.render();
  }

  /** Update font size */
  setFontSize(size: number): void {
    this.style.fontSize = size;
    this.labelText.style.fontSize = size;
    this.labelText.style.lineHeight = size * this.style.lineHeight;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private render(): void {
    const { style } = this;
    const radius = this._diameter / 2;
    const centerX = radius;
    const centerY = radius;

    // Determine gradient colors based on state
    let gradientFrom = style.gradientFrom;
    let gradientTo = style.gradientTo;

    if (this._pressed) {
      gradientFrom = style.pressGradientFrom;
      gradientTo = style.pressGradientTo;
    } else if (this._hovered) {
      gradientFrom = style.hoverGradientFrom;
      gradientTo = style.hoverGradientTo;
    }

    // Draw shadow (multi-layer blur)
    this.shadow.clear();
    const shadowCX = centerX + style.shadowOffsetX;
    const shadowCY = centerY + style.shadowOffsetY;
    const blurSteps = 5;
    for (let i = blurSteps; i >= 0; i--) {
      const expand = style.shadowBlur * (i / blurSteps);
      const alpha = style.shadowAlpha * ((blurSteps - i) / blurSteps);
      this.shadow.circle(shadowCX, shadowCY, radius + expand);
      this.shadow.fill({ color: style.shadowColor, alpha });
    }

    // Update label color based on hover state
    this.labelText.style.fill = this._hovered ? style.hoverTextColor : style.textColor;

    // Draw hover glow
    if (this._hovered && !this._pressed) {
      this.glow.clear();
      const glowSize = style.glowSize;

      // Multiple layers for soft glow (concentric circles)
      for (let i = 3; i >= 0; i--) {
        const expand = glowSize * (i / 3);
        const alpha = style.glowAlpha * (0.25 * (3 - i));
        this.glow.circle(centerX, centerY, radius + expand);
        this.glow.fill({ color: style.glowColor, alpha });
      }
      this.glow.visible = true;
    } else {
      this.glow.visible = false;
    }

    // Draw gradient background
    this.background.clear();

    // Create vertical gradient (top to bottom) using PixiJS v8 API
    // textureSpace: 'local' uses normalized coordinates (0-1)
    const gradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 }, // Top
      end: { x: 0, y: 1 }, // Bottom
      colorStops: [
        { offset: 0, color: gradientFrom },
        { offset: 1, color: gradientTo },
      ],
      textureSpace: 'local',
    });

    this.background.circle(centerX, centerY, radius);
    this.background.fill(gradient);

    // Draw border
    if (style.borderWidth > 0) {
      this.background.circle(centerX, centerY, radius);
      this.background.stroke({ width: style.borderWidth, color: style.borderColor });
    }
  }

  private onPointerOver(_event: FederatedPointerEvent): void {
    this._hovered = true;
    this.render();
  }

  private onPointerOut(_event: FederatedPointerEvent): void {
    this._hovered = false;
    this._pressed = false;
    this.render();
  }

  private onPointerDown(_event: FederatedPointerEvent): void {
    this._pressed = true;
    this.render();
  }

  private onPointerUp(_event: FederatedPointerEvent): void {
    if (this._pressed) {
      this._pressed = false;
      this.render();

      // Play click sound
      audioService.play('button_click');

      // Trigger callback
      this.onClick?.();
    }
  }

  private onPointerUpOutside(_event: FederatedPointerEvent): void {
    this._pressed = false;
    this.render();
  }
}
